import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser, useUpdateUser, useRoles } from '../../services/httpServices/queries';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Save,
  KeyRound,
  Info,
  Plus,
  X,
} from 'lucide-react';

const sectionTitleClass =
  'text-sm font-semibold text-text uppercase tracking-wide border-b border-border pb-2 mb-4';
export function UserRoles() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: userData, isLoading: userLoading, error: userError } = useUser(id || '');
  const { data: rolesData } = useRoles();
  const updateUser = useUpdateUser();

  const user = userData?.data;
  const roles = rolesData?.data || [];

  const [customRoleIds, setCustomRoleIds] = useState<string[]>([]);

  useEffect(() => {
    if (user) {
      setCustomRoleIds(user.customRoles?.map((r: any) => r.id) || []);
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      await updateUser.mutateAsync({ id, data: { customRoleIds } });
      navigate(-1);
    } catch {
      // handled by mutation
    }
  };

  if (userLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (userError || !user) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Failed to load user. Please try again.</span>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 max-w-3xl mx-auto">
      {/* Top Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-text-muted hover:text-text transition-colors text-sm cursor-pointer"
        >
          <ArrowLeft size={18} />
          Back
        </button>
      </div>

      {/* Header */}
      <div className="flex items-center gap-3">
        <KeyRound className="w-6 h-6 text-primary" />
        <h1 className="text-2xl font-bold text-text">Assign Roles</h1>
      </div>

      <div className="bg-info/10 border border-info/20 rounded-lg p-4 flex items-start gap-3 text-info text-sm">
        <Info size={16} className="mt-0.5 shrink-0" />
        <div>
          <p className="font-medium mb-1">How permissions work</p>
          <p>
            Custom Roles allow fine-grained permission control.
            Permissions are assigned to Custom Roles in the{' '}
            <button
              onClick={() => navigate('/roles')}
              className="font-semibold underline hover:text-info-dark transition-colors"
            >
              Roles page
            </button>.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-surface border border-border rounded-xl p-6 space-y-6">
        {/* User Info Banner */}
        <div className="flex items-center gap-3 p-3 bg-background rounded-lg border border-border">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
            {user.firstName?.[0]}{user.lastName?.[0]}
          </div>
          <div>
            <p className="text-sm font-medium text-text">{user.firstName} {user.lastName}</p>
            <p className="text-xs text-text-muted">@{user.username}</p>
          </div>
        </div>

        {/* Custom Roles */}
        <div>
          <p className={sectionTitleClass}>Custom Roles</p>
          <div className="space-y-3">
            {/* Selected Custom Roles */}
            <div className="flex flex-wrap gap-2 min-h-[28px]">
              {customRoleIds.length === 0 ? (
                <span className="text-xs text-text-muted italic">No custom roles assigned</span>
              ) : (
                customRoleIds.map((rid) => {
                  const role = roles.find((r: any) => r.id === rid);
                  if (!role) return null;
                  return (
                    <span
                      key={rid}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-full text-xs font-medium shadow-sm"
                    >
                      {role.name}
                      <button
                        type="button"
                        onClick={() => setCustomRoleIds(customRoleIds.filter((id) => id !== rid))}
                        className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                        title={`Remove ${role.name}`}
                      >
                        <X size={12} strokeWidth={3} />
                      </button>
                    </span>
                  );
                })
              )}
            </div>

            {/* Available Custom Roles */}
            {roles.length > 0 ? (
              <div className="border border-border rounded-xl p-4 bg-background/50">
                <p className="text-[0.6875rem] font-semibold text-text-muted uppercase tracking-wider mb-3">
                  Available Custom Roles
                </p>
                <div className="flex flex-wrap gap-2">
                  {roles.map((role: any) => {
                    if (customRoleIds.includes(role.id)) return null;
                    return (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() => setCustomRoleIds([...customRoleIds, role.id])}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-border bg-surface text-text hover:border-primary hover:text-primary hover:bg-primary/5 transition-all"
                      >
                        <Plus size={12} />
                        {role.name}
                      </button>
                    );
                  })}
                  {roles.every((r: any) => customRoleIds.includes(r.id)) && (
                    <span className="text-xs text-text-muted italic">All custom roles assigned</span>
                  )}
                </div>
              </div>
            ) : (
              <span className="text-xs text-text-muted">
                No custom roles available. Create one in the{' '}
                <button
                  type="button"
                  onClick={() => navigate('/roles')}
                  className="font-semibold underline hover:text-primary transition-colors"
                >
                  Roles page
                </button>{' '}
                first.
              </span>
            )}
          </div>
          <p className="text-xs text-text-muted mt-2 flex items-center gap-1.5">
            <Info size={13} className="text-primary shrink-0" />
            Custom roles grant fine-grained permissions configured on each role.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={updateUser.isPending}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {updateUser.isPending ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Roles
          </button>
        </div>
      </form>
    </div>
  );
}
